/**
 * Reactive store — pub/sub pattern
 * Cart persists to localStorage, other state is session-only
 */

const CART_KEY = 'airplaten_cart'

const state = {
  cart: loadCart(),
  cartOpen: false,
}

const subscribers = {}

function loadCart() {
  try {
    return JSON.parse(localStorage.getItem(CART_KEY)) || []
  } catch {
    return []
  }
}

function saveCart(items) {
  try {
    localStorage.setItem(CART_KEY, JSON.stringify(items))
  } catch {
    // localStorage unavailable
  }
}

export const store = {
  get(key) {
    return state[key]
  },

  set(key, value) {
    state[key] = value
    if (key === 'cart') saveCart(value)
    notify(key, value)
  },

  subscribe(key, fn) {
    if (!subscribers[key]) subscribers[key] = []
    subscribers[key].push(fn)
    // Immediately call with current value
    fn(state[key])
    return () => {
      subscribers[key] = subscribers[key].filter(f => f !== fn)
    }
  },

  // Cart helpers
  addToCart(product, quantity = 1) {
    const cart = [...state.cart]
    const existing = cart.find(i => i.id === product.id)
    if (existing) {
      existing.quantity = Math.min(existing.quantity + quantity, 99)
      if (!existing.image && product.images?.[0]) existing.image = product.images[0]
    } else {
      cart.push({
        id: product.id,
        name: product.name,
        price_cents: product.price_cents,
        image: product.images?.[0] || null,
        sku: product.sku,
        quantity,
      })
    }
    this.set('cart', cart)
  },

  removeFromCart(productId) {
    const cart = state.cart.filter(i => i.id !== productId)
    this.set('cart', cart)
  },

  updateQuantity(productId, quantity) {
    if (quantity <= 0) {
      this.removeFromCart(productId)
      return
    }
    const cart = state.cart.map(i =>
      i.id === productId ? { ...i, quantity: Math.min(quantity, 99) } : i
    )
    this.set('cart', cart)
  },

  refreshCartImages(products) {
    const cart = state.cart
    if (!cart.length || !products?.length) return
    let changed = false
    const updated = cart.map(item => {
      if (item.image) return item
      const product = products.find(p => p.id === item.id)
      if (product?.images?.[0]) { changed = true; return { ...item, image: product.images[0] } }
      return item
    })
    if (changed) this.set('cart', updated)
  },

  clearCart() {
    this.set('cart', [])
  },

  get cartCount() {
    return state.cart.reduce((sum, i) => sum + i.quantity, 0)
  },

  get cartTotal() {
    return state.cart.reduce((sum, i) => sum + i.price_cents * i.quantity, 0)
  },

  openCart() {
    this.set('cartOpen', true)
    document.body.style.overflow = 'hidden'
  },

  closeCart() {
    this.set('cartOpen', false)
    document.body.style.overflow = ''
  },
}

function notify(key, value) {
  if (!subscribers[key]) return
  for (const fn of subscribers[key]) {
    try { fn(value) } catch (err) { console.error('[store]', err) }
  }
}
